import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, SendHorizonal, X } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("nnm_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("nnm_chat_session", id);
  }
  return id;
}

export function ChatWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    setMessages(m => (m.length === 1 && m[0].role === "assistant" && m[0].greeting) ? [{ role: "assistant", text: t("chat.greeting"), greeting: true }] : m.length ? m : [{ role: "assistant", text: t("chat.greeting"), greeting: true }]);
  }, [t]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages(m => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            setMessages(m => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + (data.text || data.error || "") };
              return copy;
            });
          } catch { /* partial chunk */ }
        }
      }
    } catch {
      setMessages(m => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", text: t("chat.err") }; return copy; });
    }
    setBusy(false);
  };

  return (
    <>
      <button className="chat-fab" onClick={() => setOpen(!open)} aria-label="Chat" data-testid="chat-toggle-button">
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="chat-panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="chat-head">
              <span className="brand-mark">NN</span>
              <div><strong>{t("chat.headTitle")}</strong><span>{t("chat.headSub")}</span></div>
            </div>
            <div className="chat-messages" data-testid="chat-messages">
              {messages.map((m, i) => <div key={i} className={`chat-msg ${m.role}`}>{m.text}{m.role === "assistant" && busy && i === messages.length - 1 && <span className="chat-caret">▍</span>}</div>)}
              <div ref={bottomRef} />
            </div>
            <form className="chat-input" onSubmit={send} data-testid="chat-form">
              <input value={input} onChange={e => setInput(e.target.value)} placeholder={t("chat.placeholder")} data-testid="chat-input" />
              <button type="submit" aria-label="Send" disabled={busy} data-testid="chat-send-button"><SendHorizonal size={17} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
